import { useNavigate } from 'react-router-dom';
import { Drawer, Skeleton } from 'antd';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import dayjs from 'dayjs';
import { toast } from 'sonner';
import {
  faBolt,
  faCheck,
  faChevronRight,
  faCircleInfo,
  faComments,
  faCopy,
  faDownload,
  faLink,
  faListCheck,
  faPlay,
  faTag,
  faUser,
  faXmark
} from '@fortawesome/free-solid-svg-icons';
import Avatar from '../Avatar';
import CallRecording from '../CallRecording';
import StatusPill from '../StatusPill';
import { GhostBtn, PrimaryBtn } from '../Buttons';
import {
  DrawerClose,
  DrawerHeadRow,
  DrawerHeader,
  DrawerStats,
  DrawerSubtitle,
  DrawerTitle,
  PillRow,
  SectionText
} from '../DrawerParts';
import { useAuth } from '../../context/AuthContext';
import { useCallQuery, useCallRecordingQuery, useResolveCall, useToggleReviewed } from '../../queries/calls';
import { useCallActions } from '../../queries/actions';
import { useAssistantName } from '../../queries/agents';
import { P } from '../../theme/tokens';
import { laneOf } from '../../utils/actions';
import {
  ActionLink,
  Attention,
  Body,
  Bubble,
  Bubbles,
  CatTitle,
  Dot,
  Footer,
  Icon,
  Ids,
  InfoGrid,
  ReviewBtn,
  SecHead,
  Section,
  SmallBtn,
  Soft,
  Turn,
  TurnWho
} from './styled';

const BODY_STYLE = { padding: 0, display: 'flex', flexDirection: 'column', background: P.bgSubtle };

function CallFooter({ call, onClose }) {
  const { can } = useAuth();
  const resolve = useResolveCall();
  const canResolve = call?.needsAttention && can('manage_requests');

  return (
    <Footer>
      {canResolve ? (
        <PrimaryBtn type="button" onClick={() => resolve.mutate(call.id)} disabled={resolve.isPending}>
          <FontAwesomeIcon icon={faCheck} />
          {resolve.isPending ? 'Resolving…' : 'Mark resolved'}
        </PrimaryBtn>
      ) : null}
      <GhostBtn type="button" onClick={onClose}>
        Close
      </GhostBtn>
    </Footer>
  );
}

function CallActions({ callId }) {
  const navigate = useNavigate();
  const { data: actions = [] } = useCallActions(callId);
  if (!actions.length) return null;
  return (
    <Section>
      <Head icon={faListCheck} title="Agent Actions from this call" />
      {actions.map((x) => (
        <ActionLink key={x._id} type="button" onClick={() => navigate('/todos', { state: { openActionId: x._id } })}>
          <span>{deDash(x.title)}</span>
          <small style={{ marginLeft: 'auto', whiteSpace: 'nowrap' }}>{laneOf(x.lane).label}</small>
          <FontAwesomeIcon icon={faChevronRight} />
        </ActionLink>
      ))}
    </Section>
  );
}

const humanize = (v) => {
  const t = String(v || '').replace(/[-_]+/g, ' ').trim();
  return t ? t.charAt(0).toUpperCase() + t.slice(1) : '';
};
const STATUS_COLOR = { completed: P.success, failed: P.danger, no_answer: P.warning, voicemail: P.warning, in_progress: P.info };
const OUTCOME = { true: 'Successful', false: 'Not successful' };
const stamp = (d) => (d ? dayjs(d).format('MMM D, YYYY h:mm:ss A') : '—');

const copy = (text, label) =>
  navigator.clipboard
    .writeText(text)
    .then(() => toast.success(`${label} copied`))
    .catch(() => toast.error(`Could not copy the ${label.toLowerCase()}`));

function Head({ icon, title, right }) {
  return (
    <SecHead>
      <FontAwesomeIcon icon={icon} />
      <h3>{title}</h3>
      {right}
    </SecHead>
  );
}

function Recording({ callId }) {
  const { data } = useCallRecordingQuery(callId);
  const url = data?.url;
  return (
    <Section>
      <Head
        icon={faPlay}
        title="Recording"
        right={
          url ? (
            <PillRow>
              <SmallBtn type="button" onClick={() => copy(url, 'Recording link')}>
                <FontAwesomeIcon icon={faLink} />
                Copy link
              </SmallBtn>
              <SmallBtn as="a" href={url} download target="_blank" rel="noreferrer">
                <FontAwesomeIcon icon={faDownload} />
                Download
              </SmallBtn>
            </PillRow>
          ) : null
        }
      />
      <CallRecording callId={callId} />
    </Section>
  );
}

function Transcript({ call }) {
  const agent = useAssistantName();
  const turns = call.transcript;
  const text = turns.map((t) => `${t.ai ? agent : 'Caller'}: ${t.text}`).join('\n');
  return (
    <Section>
      <Head
        icon={faComments}
        title="Transcript"
        right={
          turns.length ? (
            <>
              <StatusPill color={P.subtle}>{turns.length} messages</StatusPill>
              <Icon type="button" aria-label="Copy transcript" title="Copy transcript" onClick={() => copy(text, 'Transcript')}>
                <FontAwesomeIcon icon={faCopy} />
              </Icon>
            </>
          ) : null
        }
      />
      {turns.length ? (
        <Bubbles>
          {turns.map((t, i) => (
            <Turn key={i} $ai={t.ai}>
              <TurnWho $ai={t.ai}>
                <FontAwesomeIcon icon={t.ai ? faBolt : faUser} />
                {t.ai ? deDash(agent) : call.name}
              </TurnWho>
              <Bubble $ai={t.ai}>{t.text}</Bubble>
            </Turn>
          ))}
        </Bubbles>
      ) : (
        <SectionText>No transcript available.</SectionText>
      )}
    </Section>
  );
}

const digits = (v) => String(v || '').replace(/\D/g, '');

// ponytail: cosmetic band-aid over backend text; real fix is the summary prompt / agent name field
const deDash = (s) => (typeof s === 'string' ? s.replace(/\s*—\s*/g, ', ') : s || '');
// strip the AI's self-narration sentence; heuristic, backend prompt is the real fix
const stripAiNarration = (s) =>
  deDash(s)
    .replace(/(^|\s)[^.?!]*\bAI will\b[^.?!]*[.?!]/gi, ' ')
    .replace(/\s{2,}/g, ' ')
    .trim();

function CallDetail({ call, onClose }) {
  const toggle = useToggleReviewed();
  const urgent = /high|urgent|emergency/i.test(call.urgency);
  const agentShort = String(useAssistantName()).split(/\s*—\s*/)[0].trim();
  const { data: actions = [] } = useCallActions(call.id);

  return (
    <>
      <DrawerHeader>
        <DrawerHeadRow>
          <Avatar name={call.name} size={44} color="rgba(255,255,255,0.18)" text={P.white} />
          <div style={{ minWidth: 0 }}>
            <DrawerTitle>{call.name}</DrawerTitle>
            {digits(call.phone) !== digits(call.name) && <DrawerSubtitle>{call.phone}</DrawerSubtitle>}
          </div>
          <DrawerClose type="button" onClick={onClose} aria-label="Close call details">
            <FontAwesomeIcon icon={faXmark} />
          </DrawerClose>
        </DrawerHeadRow>
        <DrawerStats>
          <div>
            <dt>When</dt>
            <dd>{`${call.date} · ${call.time}`}</dd>
          </div>
          <div>
            <dt>Length</dt>
            <dd>{call.duration}</dd>
          </div>
          {call.role && (
            <div>
              <dt>Caller</dt>
              <dd>{call.role}</dd>
            </div>
          )}
          {call.language && (
            <div>
              <dt>Language</dt>
              <dd>{call.language}</dd>
            </div>
          )}
        </DrawerStats>
      </DrawerHeader>

      <Body>
        {call.needsAttention && (
          <Attention role="status">
            <b>This call needs a person</b>
            Follow up with the caller, then mark it resolved.
          </Attention>
        )}

        <Section>
          <Head icon={faCircleInfo} title="Call information" />
          <InfoGrid>
            <div>
              <dt>From</dt>
              <dd>{call.from || '—'}</dd>
            </div>
            <div>
              <dt>To</dt>
              <dd>{call.to || '—'}</dd>
            </div>
            <div>
              <dt>Status</dt>
              <dd>
                {call.rawStatus ? (
                  <StatusPill color={STATUS_COLOR[call.rawStatus] || P.subtle}>{humanize(call.rawStatus)}</StatusPill>
                ) : (
                  '—'
                )}
              </dd>
            </div>
            <div>
              <dt>Outcome</dt>
              <dd>{OUTCOME[call.outcome] || humanize(call.outcome) || '—'}</dd>
            </div>
            <div>
              <dt>Start time</dt>
              <dd>{stamp(call.startedAt)}</dd>
            </div>
            <div>
              <dt>End time</dt>
              <dd>{stamp(call.endedAt)}</dd>
            </div>
            {call.endedReason && (
              <div>
                <dt>Ended reason</dt>
                <dd>
                  <StatusPill color={P.subtle}>{humanize(call.endedReason)}</StatusPill>
                </dd>
              </div>
            )}
            {call.property && (
              <div>
                <dt>Property / unit</dt>
                <dd>{call.property}</dd>
              </div>
            )}
          </InfoGrid>
        </Section>

        <Section>
          <Head
            icon={faTag}
            title="Category"
            right={
              <ReviewBtn
                type="button"
                $on={call.reviewed}
                aria-pressed={call.reviewed}
                onClick={() => toggle.mutate({ id: call.id, reviewed: !call.reviewed })}
              >
                {call.reviewed ? 'Reviewed ✓' : 'Mark reviewed'}
              </ReviewBtn>
            }
          />
          <Soft>
            <CatTitle>
              <Dot $c={call.type.color} />
              {call.type.label}
              {call.intent && <span style={{ fontWeight: 400, color: P.muted }}>· {humanize(call.intent)}</span>}
            </CatTitle>
            {call.why && <div style={{ color: P.muted }}>{call.why}</div>}
            <PillRow>
              {call.callerRole && <StatusPill color={P.subtle}>{humanize(call.callerRole)}</StatusPill>}
              {call.urgency && <StatusPill color={urgent ? P.danger : P.subtle}>Urgency: {call.urgency.toLowerCase()}</StatusPill>}
              {call.status && <StatusPill color={call.status.color}>{call.status.label}</StatusPill>}
            </PillRow>
          </Soft>
        </Section>

        <Section>
          <Head icon={faBolt} title="AI Summary" />
          <Soft>
            {stripAiNarration(call.summary) || 'No summary for this call yet.'}
            {actions.length > 0 && (
              <div style={{ color: P.muted, marginTop: 6 }}>{agentShort} has created agent action below:</div>
            )}
          </Soft>
        </Section>

        <CallActions callId={call.id} />

        {call.hasRecording && <Recording callId={call.id} />}

        <Transcript call={call} />

        <Ids>
          Call ID: {call.id}
          {call.providerCallId ? ` · Provider: ${call.providerCallId}` : ''}
        </Ids>
      </Body>
    </>
  );
}

export default function CallDrawer({ callId, onClose }) {
  const { call, isLoading } = useCallQuery(callId);

  return (
    <Drawer
      open={Boolean(callId)}
      onClose={onClose}
      width={480}
      zIndex={1100}
      closable={false}
      title={null}
      styles={{ body: BODY_STYLE, footer: { padding: '14px 22px' } }}
      footer={call ? <CallFooter call={call} onClose={onClose} /> : null}
    >
      {isLoading || !call ? (
        <div style={{ padding: 22 }}>
          <Skeleton avatar active paragraph={{ rows: 8 }} />
        </div>
      ) : (
        <CallDetail call={call} onClose={onClose} />
      )}
    </Drawer>
  );
}
